import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store';
import apiService from '../../services/api';
import { User } from '../../types';

interface UserManagementProps {
  show: boolean;
  onHide: () => void;
  inline?: boolean; // 新增：是否为内嵌模式
}

const UserManagement: React.FC<UserManagementProps> = ({ show, onHide, inline = false }) => {
  const { user: currentUser } = useAppStore();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  // 避免未使用变量警告 - 这些是为未来功能预留的
  void selectedUser;
  void showUserModal;


  useEffect(() => {
    if (show && currentUser?.is_admin) {
      loadUsers();
    }
  }, [show, currentUser]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getUsers();
      if (response.success) {
        setUsers(response.users || []);
      } else {
        setError(response.error || '获取用户列表失败');
      }

    } catch (error: any) {
      setError(error.message || '加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (user: User, action: string) => {
    try {
      setError('');

      switch (action) {
        case 'edit':
          setSelectedUser(user);
          setShowUserModal(true);
          break;
        case 'toggle_active':
          const activeResponse = await apiService.toggleUserActive(user.id);
          if (activeResponse.success) {
            // 更新本地用户列表
            setUsers(users.map(u =>
              u.id === user.id ? { ...u, is_active: !u.is_active } : u
            ));
            alert(activeResponse.message);
          } else {
            setError(activeResponse.error || '操作失败');
          }
          break;
        case 'toggle_admin':
          const adminResponse = await apiService.toggleUserAdmin(user.id);
          if (adminResponse.success) {
            // 更新本地用户列表
            setUsers(users.map(u =>
              u.id === user.id ? { ...u, is_admin: !u.is_admin } : u
            ));
            alert(adminResponse.message);
          } else {
            setError(adminResponse.error || '操作失败');
          }
          break;
        case 'reset_password':
          const newPassword = prompt('请输入新密码（至少6位，包含字母和数字）:');
          if (newPassword) {
            const resetResponse = await apiService.resetUserPassword(user.id, newPassword);
            if (resetResponse.success) {
              alert(resetResponse.message);
            } else {
              setError(resetResponse.error || '重置密码失败');
            }
          }
          break;
        case 'delete':
          if (user.id === currentUser?.id) {
            setError('不能删除自己的账户');
            return;
          }
          if (confirm(`确认删除用户 ${user.username}？此操作不可恢复！`)) {
            const deleteResponse = await apiService.deleteUser(user.id);
            if (deleteResponse.success) {
              // 从本地用户列表中移除
              setUsers(users.filter(u => u.id !== user.id));
              alert(deleteResponse.message);
            } else {
              setError(deleteResponse.error || '删除用户失败');
            }
          }
          break;
      }
    } catch (error: any) {
      setError(error.message || '操作失败');
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '从未';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  if (!show || !currentUser?.is_admin) return null;

  // 内嵌模式的内容
  const content = (
    <div className={inline ? "admin-inline-body" : "admin-content"}>
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <i className="fas fa-spinner fa-spin"></i>
              <span className="ms-2">加载中...</span>
            </div>
          ) : (
            <div className="users-table-container">
              <div className="table-header">
                <h4>用户列表 ({users.length})</h4>
              </div>

              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>用户名</th>
                      <th>手机号</th>
                      <th>状态</th>
                      <th>权限</th>
                      <th>API配置</th>
                      <th>注册时间</th>
                      <th>最后登录</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className={user.id === currentUser?.id ? 'current-user' : ''}>
                        <td>{user.id}</td>
                        <td>
                          <div className="user-info">
                            <span className="username">{user.username}</span>
                            {user.id === currentUser?.id && (
                              <span className="badge badge-info">当前用户</span>
                            )}
                          </div>
                        </td>
                        <td>{user.phone}</td>
                        <td>
                          <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {user.is_active ? '正常' : '禁用'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${user.is_admin ? 'badge-warning' : 'badge-secondary'}`}>
                            {user.is_admin ? '管理员' : '普通用户'}
                          </span>
                        </td>
                        <td>
                          <div className="api-status">
                            <span className={`api-indicator ${user.has_qianwen_key ? 'active' : ''}`} title="LLM模型API">
                              <i className="fas fa-robot"></i>
                            </span>
                            <span className={`api-indicator ${user.has_github_token ? 'active' : ''}`} title="GitHub Token">
                              <i className="fab fa-github"></i>
                            </span>
                          </div>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>{formatDate(user.last_login)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleUserAction(user, 'edit')}
                              title="编辑"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className={`btn btn-sm ${user.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                              onClick={() => handleUserAction(user, 'toggle_active')}
                              title={user.is_active ? '禁用' : '启用'}
                            >
                              <i className={`fas ${user.is_active ? 'fa-ban' : 'fa-check'}`}></i>
                            </button>
                            {user.id !== currentUser?.id && (
                              <>
                                <button
                                  className={`btn btn-sm ${user.is_admin ? 'btn-outline-secondary' : 'btn-outline-warning'}`}
                                  onClick={() => handleUserAction(user, 'toggle_admin')}
                                  title={user.is_admin ? '取消管理员' : '设为管理员'}
                                >
                                  <i className="fas fa-crown"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleUserAction(user, 'delete')}
                                  title="删除用户"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
    </div>
  );

  // 根据模式返回不同的结构
  if (inline) {
    return content;
  }

  return (
    <div className="admin-overlay" onClick={handleOverlayClick}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <h3>
            <i className="fas fa-users-cog"></i>
            用户管理
          </h3>
          <button className="admin-close" onClick={onHide} title="关闭">
            <i className="fas fa-times"></i>
          </button>
        </div>
        {content}
      </div>
    </div>
  );
};

export default UserManagement;
